import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { BettingType, StakeErrorType, StyleObj } from '../../@types';
import NumberInput from './NumberInput';
import { getLocalizedBettingType, parseNumberWithDecimals, prepareStakeMessages } from '../../helpers';
import { useBetslipTicketType } from '../../stores/betslip/selectors';
import { useGlobalTicketConditions } from '../../queries';
import { useEffect } from 'react';

import OddsDisplay from './OddsDisplay';
import { CurrencySymbol } from './CurrencySymbol';
import useLocalization from '../../hooks/useLocalization';
import { useSkinStyles } from '../../hooks/useSkinStyles';
import { SKIN, WalletType } from '../../types/enums';

import { mergeSx } from '../../utils/styles';

const defaultStyles: StyleObj = {
  betslipInfo: {
    background: (theme) => theme.palette.neutral[100],
    py: 1,
    px: 1.5,
    height: 40,
  },
  stakeInput: {
    width: 90,
    height: 40,
    flexDirection: 'row',
    '& fieldset': { border: 'none' },
    '& .MuiInputBase-input': {
      color: (theme) => theme.palette.primary.main,
      textAlign: 'right',
      fontWeight: 600,
      fontSize: 14,
    },

    background: 'white',
  },
};

const neobetStyles: StyleObj = {
  ...defaultStyles,
  stakeInput: {
    width: 90,
    '& .MuiInputBase-root': {
      height: 40,
      borderRadius: 2.5,
      border: '1px solid',
      borderColor: 'divider',
      backgroundColor: 'neutral.700',
      boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.04)',
      '&::before, &::after': {
        display: 'none',
      },
      '&:hover:not(.Mui-disabled)::before': {
        display: 'none',
      },
    },
    '& .MuiInputBase-input': {
      color: 'text.primary',
      textAlign: 'right',
      fontWeight: 700,
      fontSize: 14,
      px: 1.5,
      py: 1.125,
    },
  },
};

type BetslipInputProps = {
  bettingType: BettingType;
  odds?: string | number;
  numberOfCombinations?: number;
  value: string;
  onChange: (value: string) => void;
  errorMessage?: StakeErrorType;
  selectedWallet: WalletType;
};

const BetslipInput = ({
  onChange,
  value,
  bettingType,
  odds,
  numberOfCombinations,
  errorMessage,
  selectedWallet,
}: BetslipInputProps) => {
  const betslipTicketType = useBetslipTicketType();
  const { data: globalTicketConditions } = useGlobalTicketConditions();
  const styles = useSkinStyles({
    [SKIN.BET365]: defaultStyles,
    [SKIN.GOLA]: defaultStyles,
    [SKIN.NEOBET]: neobetStyles,
  });

  const stakeMessage = prepareStakeMessages(betslipTicketType, selectedWallet, globalTicketConditions, errorMessage);
  const { type: stakeErrorType, amount: stakeErrorAmount } = stakeMessage ?? {};

  const { t } = useLocalization();
  const localizedBettingType = getLocalizedBettingType(t, bettingType);

  useEffect(() => {
    onChange(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [odds, numberOfCombinations, selectedWallet]);

  return (
    <Stack
      direction='row'
      sx={{
        alignItems: 'center',
      }}
    >
      <Stack
        direction='row'
        sx={mergeSx(
          {
            alignItems: 'center',
            justifyContent: 'space-between',
            flexGrow: 1,
          },
          styles.betslipInfo
        )}
      >
        <Typography
          variant='h5'
          sx={{
            color: 'neutral.600',
          }}
        >
          {localizedBettingType}{' '}
          {odds && (
            <>
              (
              <OddsDisplay
                value={odds}
                variant='h5'
                sx={{
                  color: 'neutral.600',
                }}
              />
              )
            </>
          )}
        </Typography>
        {stakeErrorType && (
          <Typography
            variant='body3'
            sx={{
              fontSize: 9,
              textAlign: 'center',
              color: 'error.600',
            }}
          >
            {t(stakeErrorType)} <CurrencySymbol />
            {stakeErrorAmount && parseNumberWithDecimals(stakeErrorAmount, 2)}
          </Typography>
        )}
        <Typography
          variant='body2'
          sx={{
            color: 'neutral.600',
          }}
        >
          {numberOfCombinations}x
        </Typography>
      </Stack>
      <NumberInput
        size='small'
        variant='standard'
        placeholder={t('stake')}
        sx={styles.stakeInput}
        onChange={onChange}
        value={value as string}
      />
    </Stack>
  );
};

export default BetslipInput;
